import { Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { LineItem, LineSearchOptions } from '@models/custom-fields.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { LineService } from '@services/line/line.service';
import {
  Observable,
  OperatorFunction,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  of,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import { DomSanitizerPipe, TdsTypeAheadComponent } from 'tangoe-design-system';

@Component({
  selector: 'ts-line-select',
  templateUrl: './line-select.component.html',
  imports: [DomSanitizerPipe, TdsTypeAheadComponent, TranslationPipe]
})
export class LineSelectComponent {
  @Input() id: string;
  @Input() label: string;
  @Input() name: string;
  @Input() help_text: string = '';
  @Input() required: boolean = false;
  @Input() control: FormControl;
  @Input() header_text?: string;

  no_results_found: boolean = false;
  loading: boolean = false;
  input_format = (item: LineItem) => item.display_value;
  private blur$: Subject<void> = new Subject<void>();

  constructor(private line_service: LineService) {}

  search: OperatorFunction<string, readonly Partial<LineItem>[]> = (term$: Observable<string>) =>
    merge(
      term$.pipe(
        debounceTime(100),
        tap(() => (this.loading = this.no_results_found = false)),
        distinctUntilChanged(),
        switchMap((term: string) => {
          if (term.length < 3) return of([]);

          this.loading = true;
          return this.line_service.search(term).pipe(
            map((response: LineSearchOptions) => {
              this.loading = false;

              if (response.items.length > 0) {
                return response.items.map((item: LineItem) => ({
                  ...item,
                  display_value: `${item.line_number} | ${item.carrier} | ${item.person_name} | ${item.person_email}`
                }));
              }

              this.no_results_found = true;
              return [];
            }),
            catchError(() => {
              this.loading = false;
              return of([]);
            }),
            takeUntil(this.blur$)
          );
        })
      ),
      this.blur$.pipe(map((): readonly Partial<LineItem>[] => []))
    );

  onBlur(): void {
    this.loading = this.no_results_found = false;
    this.blur$.next();
  }
}
